import { useState, useEffect, useCallback } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { formatPHP } from '../lib/utils';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  RefreshCw, Search, FileText, Loader2, XCircle, AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Row color: green by default, only flag when something is wrong
function getRowColor(chk) {
  if (chk.status === 'bounced_final')        return 'bg-red-50/50';
  if (chk.status === 'bounced_redepositable')return 'bg-amber-50/50';
  return ''; // all active checks are plain/green
}

const STATUS_CONFIG = {
  active:                 { label: 'Active',         color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  bounced_redepositable:  { label: 'Bounced — Retry',color: 'bg-amber-100 text-amber-700 border-amber-200'       },
  bounced_final:          { label: 'Bounced — Final',color: 'bg-red-100 text-red-700 border-red-200'             },
};

export default function CheckMonitoringPage() {
  const { effectiveBranchId, currentBranch, isConsolidatedView } = useAuth();
  const today = new Date().toISOString().slice(0, 10);

  const [checks, setChecks]       = useState([]);
  const [summary, setSummary]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [showBounced, setShowBounced] = useState(false); // toggle to show bounced history
  const [bounceReasons, setBounceReasons] = useState([]);

  // Bounce dialog
  const [bounceDialog, setBounceDialog] = useState(null);
  const [reasonCode, setReasonCode] = useState('');
  const [canRedeposit, setCanRedeposit] = useState(false);
  const [reversalFundSource, setReversalFundSource] = useState('bank');
  const [bouncePin, setBouncePin] = useState('');
  const [bouncing, setBouncing] = useState(false);

  // Branch param: null = all branches (admin consolidated), specific id otherwise
  const branchParam = effectiveBranchId === 'all' ? undefined : (effectiveBranchId || currentBranch?.id);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = showBounced ? 'bounced_redepositable,bounced_final' : 'active,bounced_redepositable';
      const params = { limit: 300, status: statusParam };
      if (branchParam) params.branch_id = branchParam;

      const [checksRes, summaryRes, reasonsRes] = await Promise.all([
        api.get(`${API}/api/checks`, { params }),
        api.get(`${API}/api/checks/dashboard-summary`, { params: branchParam ? { branch_id: branchParam } : {} }),
        bounceReasons.length === 0
          ? api.get(`${API}/api/checks/bounce-reasons`)
          : Promise.resolve({ data: bounceReasons }),
      ]);
      setChecks(checksRes.data.checks || []);
      setSummary(summaryRes.data);
      if (bounceReasons.length === 0) setBounceReasons(reasonsRes.data || []);
    } catch { toast.error('Failed to load checks'); }
    setLoading(false);
  }, [branchParam, showBounced]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleBounce = async () => {
    if (!bounceDialog || !reasonCode) { toast.error('Select a bounce reason'); return; }
    if (!bouncePin.trim()) { toast.error('Manager PIN required'); return; }
    setBouncing(true);
    try {
      const reason = bounceReasons.find(r => r.code === reasonCode);
      // 2nd bounce is a SUGGESTION to go final, never forced — the operator
      // decides (banks often allow 2-3 presentations).
      const isFinal = reason?.auto_final || !canRedeposit;
      // Payment Wing P2.4: bounces route through the Correction Center door —
      // same battle-tested flow underneath, but a FINAL bounce now mints COR
      // paper (chain + registry + tier record) instead of moving money bare.
      const res = await api.post(`${API}/api/corrections/doors/check-bounce`, {
        check_id: bounceDialog.id,
        reason_code: reasonCode,
        can_redeposit: !isFinal,
        pin: bouncePin,
        reversal_fund_source: reversalFundSource,
      });
      toast.success(res.data.message);
      setBounceDialog(null);
      setReasonCode(''); setBouncePin(''); setCanRedeposit(false); setReversalFundSource('bank');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bounce failed'); }
    setBouncing(false);
  };

  const selectedReason  = bounceReasons.find(r => r.code === reasonCode);
  const isForcedFinal   = !!selectedReason?.auto_final;
  const isSecondBounce  = (bounceDialog?.bounces?.length || 0) >= 1;
  const willBeFinal     = isForcedFinal || !canRedeposit;

  const viewLabel = isConsolidatedView ? 'All Branches' : (currentBranch?.name || '');
  const filtered  = checks.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.customer_name || '').toLowerCase().includes(q)
      || (c.check_number || '').toLowerCase().includes(q)
      || (c.bank_name || '').toLowerCase().includes(q);
  });

  return (
    <div className="flex flex-col h-full" data-testid="check-monitoring-page">

      {/* ── Header ── */}
      <div className="px-5 pt-4 pb-3 border-b border-slate-200 bg-white flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-lg font-bold" style={{ fontFamily: 'Manrope' }}>Check Monitoring</h1>
          <p className="text-xs text-slate-500">
            {viewLabel} — PDC & check payment tracker
            {isConsolidatedView && <span className="ml-2 text-[10px] bg-slate-100 text-slate-500 rounded px-1.5 py-0.5">All Branches</span>}
          </p>
        </div>
        <Button variant="outline" size="sm" className="h-8 gap-1" onClick={loadData} disabled={loading}>
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
        </Button>
      </div>

      {/* ── Summary Cards ── */}
      {summary && (
        <div className="px-5 py-3 grid grid-cols-2 sm:grid-cols-4 gap-2 border-b border-slate-100 bg-slate-50">
          {[
            { key: 'active',               label: 'Active Checks',      color: 'text-emerald-700' },
            { key: 'received_this_week',   label: 'Received This Week', color: 'text-blue-700'    },
            { key: 'bounced_redepositable',label: 'Bounced — Retry',    color: 'text-amber-600'   },
            { key: 'bounced_final',        label: 'Bounced — Final',    color: 'text-red-700'     },
          ].map(({ key, label, color }) => (
            <div key={key} className="bg-white rounded-lg border border-slate-200 px-3 py-2 text-center">
              <p className="text-[10px] text-slate-400 uppercase font-medium">{label}</p>
              <p className={`text-sm font-bold ${color}`}>{summary[key]?.count ?? 0}</p>
              <p className="text-[10px] text-slate-500">{formatPHP(summary[key]?.total ?? 0)}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── Filters ── */}
      <div className="px-5 py-2 flex items-center gap-3 border-b border-slate-100 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search customer, check #, bank..."
            className="pl-8 h-8 text-sm" data-testid="check-search" />
        </div>
        <button onClick={() => setShowBounced(v => !v)}
          className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
            showBounced ? 'bg-red-50 border-red-300 text-red-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
          }`}>
          {showBounced ? 'Showing bounced' : 'Show bounced history'}
        </button>
      </div>

      {/* ── Table ── */}
      <ScrollArea className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
            <FileText size={40} className="mb-3 opacity-30" />
            <p className="text-sm">No checks found</p>
            <p className="text-xs mt-1 text-slate-400">
              Check payments recorded via Receive Payments will appear here
            </p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="checks-table">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
              <tr>
                {isConsolidatedView && (
                  <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-500 uppercase">Branch</th>
                )}
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-slate-500 uppercase">Customer</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-slate-500 uppercase">Check #</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-slate-500 uppercase">Bank</th>
                <th className="px-4 py-2.5 text-right text-[10px] font-semibold text-slate-500 uppercase">Amount</th>
                <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-slate-500 uppercase">Received</th>
                <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-slate-500 uppercase">Check Date</th>
                <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-slate-500 uppercase">Status</th>
                <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-slate-500 uppercase w-24"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(chk => {
                const cfg   = STATUS_CONFIG[chk.status] || STATUS_CONFIG.active;
                const rowBg = getRowColor(chk);
                const isPDC = chk.check_date > today;
                const lastBounce = (chk.bounces || []).slice(-1)[0];
                return (
                  <tr key={chk.id}
                    className={`border-b border-slate-100 hover:bg-slate-50/60 transition-colors ${rowBg}`}
                    data-testid={`check-row-${chk.id}`}>
                    {isConsolidatedView && (
                      <td className="px-3 py-2.5 text-xs text-slate-400">{chk.branch_id}</td>
                    )}
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-slate-800">{chk.customer_name}</p>
                      {chk.pp_number && <p className="text-[10px] text-slate-400">{chk.pp_number}</p>}
                    </td>
                    <td className="px-4 py-2.5 font-mono font-medium text-slate-700">{chk.check_number || '—'}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">{chk.bank_name || '—'}</td>
                    <td className="px-4 py-2.5 text-right font-bold text-slate-800">{formatPHP(chk.amount)}</td>
                    <td className="px-4 py-2.5 text-center text-xs text-slate-500">{chk.received_date}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`text-xs font-medium ${isPDC ? 'text-amber-600' : 'text-slate-600'}`}>
                        {chk.check_date}
                      </span>
                      {isPDC && (
                        <span className="ml-1 text-[9px] bg-amber-100 text-amber-700 rounded px-1 py-0.5">PDC</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <Badge variant="outline" className={`text-[10px] ${cfg.color}`}>{cfg.label}</Badge>
                      {lastBounce && (
                        <p className="text-[9px] text-slate-400 mt-0.5 max-w-[120px] truncate" title={lastBounce.reason_label}>
                          {lastBounce.reason_label}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      {/* Only action: Mark Bounced. Shown only for active checks. */}
                      {chk.status !== 'bounced_final' && (
                        <button
                          onClick={() => { setBounceDialog(chk); setReasonCode(''); setBouncePin(''); setCanRedeposit(false); }}
                          data-testid={`bounce-btn-${chk.id}`}
                          className="text-[10px] text-red-600 hover:text-red-800 border border-red-200 hover:border-red-400 rounded px-2 py-1 transition-colors bg-white hover:bg-red-50 font-medium">
                          Mark Bounced
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </ScrollArea>

      {/* ── Bounce Dialog ── */}
      <Dialog open={!!bounceDialog} onOpenChange={(o) => { if (!o && !bouncing) setBounceDialog(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <XCircle size={18} className="text-red-500" /> Record Check Bounce
            </DialogTitle>
          </DialogHeader>
          {bounceDialog && (
            <div className="space-y-4 pt-1">
              {/* Summary */}
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 space-y-0.5">
                <p className="text-xs font-semibold text-slate-600">{bounceDialog.customer_name}</p>
                <p className="text-sm font-bold text-slate-800">
                  Check #{bounceDialog.check_number} — {formatPHP(bounceDialog.amount)}
                </p>
                <p className="text-xs text-slate-400">
                  Check Date: {bounceDialog.check_date}
                  {bounceDialog.bank_name ? ` · ${bounceDialog.bank_name}` : ''}
                </p>
                {isSecondBounce && (
                  <Badge className="text-[9px] bg-amber-100 text-amber-700 mt-1">
                    Bounce #{(bounceDialog.bounces?.length || 0) + 1} — consider marking final
                  </Badge>
                )}
              </div>

              {/* Reason */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-600">Bounce Reason *</Label>
                <Select value={reasonCode} onValueChange={setReasonCode}>
                  <SelectTrigger className="h-9" data-testid="bounce-reason-select">
                    <SelectValue placeholder="Select reason..." />
                  </SelectTrigger>
                  <SelectContent>
                    {bounceReasons.map(r => (
                      <SelectItem key={r.code} value={r.code}>
                        {r.label}{r.auto_final ? ' — Final' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Redeposit choice — shown for every non-auto-final bounce.
                  A 2nd bounce only SUGGESTS finality; the operator decides. */}
              {reasonCode && !selectedReason?.auto_final && (
                <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                  <p className="text-xs font-semibold text-slate-700">Can this check still be redeposited?</p>
                  <div className="flex gap-2">
                    <button onClick={() => setCanRedeposit(true)} data-testid="can-redeposit-yes"
                      className={`flex-1 py-2 rounded-md text-xs font-semibold border transition-colors ${
                        canRedeposit ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}>
                      Yes — will redeposit
                    </button>
                    <button onClick={() => setCanRedeposit(false)} data-testid="can-redeposit-no"
                      className={`flex-1 py-2 rounded-md text-xs font-semibold border transition-colors ${
                        !canRedeposit ? 'bg-red-500 text-white border-red-500' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}>
                      No — final, reverse now
                    </button>
                  </div>
                </div>
              )}

              {/* Reversal source — final bounces only. By the time a check is
                  declared dead the paper has long left the till, so the bank
                  is the default; cashier covers the never-deposited case. */}
              {reasonCode && willBeFinal && (
                <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                  <p className="text-xs font-semibold text-slate-700">Where does the money come back from?</p>
                  <div className="flex gap-2">
                    <button onClick={() => setReversalFundSource('bank')} data-testid="reversal-source-bank"
                      className={`flex-1 py-2 rounded-md text-xs font-semibold border transition-colors ${
                        reversalFundSource === 'bank' ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}>
                      Bank — check was deposited
                    </button>
                    <button onClick={() => setReversalFundSource('cashier')} data-testid="reversal-source-cashier"
                      className={`flex-1 py-2 rounded-md text-xs font-semibold border transition-colors ${
                        reversalFundSource === 'cashier' ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}>
                      Cashier — still in the till
                    </button>
                  </div>
                </div>
              )}

              {/* Impact notice */}
              {reasonCode && (
                <div className={`rounded-lg px-3 py-2.5 text-xs leading-relaxed ${
                  willBeFinal
                    ? 'bg-red-50 border border-red-200 text-red-700'
                    : 'bg-amber-50 border border-amber-200 text-amber-700'
                }`}>
                  {willBeFinal ? (
                    <>
                      <strong>Final — the balance comes back today.</strong> A reversal entry
                      is posted today on each invoice (the original payment day stays sealed),
                      and {reversalFundSource === 'bank' ? 'the bank' : 'the cashier drawer'} is
                      debited. Shows on today's Z-Report; reversal SMS sent to the customer.
                    </>
                  ) : (
                    <>
                      <strong>No money moves yet.</strong> The invoice stays paid while the
                      check can still clear. Logged as an action item on today's Z-Report and
                      in Check Monitoring; bank penalty notice sent to the customer.
                    </>
                  )}
                </div>
              )}

              {/* PIN */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-600">Manager PIN *</Label>
                <Input type="password" value={bouncePin} onChange={e => setBouncePin(e.target.value)}
                  placeholder="Enter manager PIN" className="h-9" data-testid="bounce-pin-input"
                  onKeyDown={e => e.key === 'Enter' && handleBounce()} />
              </div>

              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1 h-9"
                  onClick={() => setBounceDialog(null)} disabled={bouncing}>
                  Cancel
                </Button>
                <Button data-testid="bounce-confirm-btn"
                  className={`flex-1 h-9 font-semibold ${
                    willBeFinal ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'
                  }`}
                  onClick={handleBounce} disabled={bouncing || !reasonCode}>
                  {bouncing ? <Loader2 size={14} className="animate-spin mr-1" /> : <XCircle size={14} className="mr-1" />}
                  {bouncing ? 'Processing…'
                    : willBeFinal ? 'Confirm — Final & Reverse'
                    : 'Confirm — Will Redeposit'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
